import React, { useEffect, useState, useMemo } from 'react';
import "./Upload.css";
import "../App.css";
import { CdsButton } from '@cds/react/button';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
const Upload = () => {
  const navigate = useNavigate();
  const [files, setFiles] = useState([]);
  const [author, setAuthor] = useState('');
  const [uploadDate, setUploadDate] = useState('');
  const [country, setCountry] = useState('');
  const [category, setCategory] = useState('');
  const [folderName, setFolderName] = useState(''); // Add state for folder name
  const [allowSharedAccess, setAllowSharedAccess] = useState(false); // Add state for shared access
  const [allFolders, setAllFolders] = useState([]); // All folders for comprehensive selection
  const [availableFolders, setAvailableFolders] = useState([]); // Add state for available folders
  const [selectedParentFolder, setSelectedParentFolder] = useState(''); // Add state for selected parent folder
  const [selectedUploadFolder, setSelectedUploadFolder] = useState(''); // Selected folder for file upload
  const [uploadMode, setUploadMode] = useState('create'); // 'create' for new folder, 'existing' for existing folder
  const [isAuthenticatedState, setIsAuthenticatedState] = useState(false);
  const [isPublic, setIsPublic] = useState(false);
  const [workpackage, setWorkpackage] = useState('');
  const [isMeeting, setIsMeeting] = useState(false);
  const [isDeliverable, setIsDeliverable] = useState(false);
  const [isContactList, setIsContactList] = useState(false);
  const [isPromotion, setIsPromotion] = useState(false);
  const [isReport, setIsReport] = useState(false);
  const [isPublication, setIsPublication] = useState(false);
  const [isTemplate, setIsTemplate] = useState(false);

  const { getAccessTokenSilently, isAuthenticated, loginWithRedirect, user } = useAuth0();

  const api = useMemo(() => axios.create({
    baseURL: process.env.REACT_APP_API_BASE_URL,
  }), []);

  useEffect(() => {
    setUploadDate(new Date().toISOString().split('T')[0]);
    setIsAuthenticatedState(isAuthenticated);
    if (user && user.name) {
      setAuthor(user.nickname.replace('.', ' ').replace(/\b\w/g, char => char.toUpperCase())); // Set the author to the logged-in user's name with spaces instead of dots and capitalize each word
    }
    
    // Fetch all folders for comprehensive selection
    const fetchAllFolders = async () => {
      if (isAuthenticated) {
        try {
          const token = await getAccessTokenSilently({
            authorizationParams: {
              audience: process.env.REACT_APP_AUTH0_AUDIENCE,
              scope: 'read:folders',
            },
          });
          const response = await api.get('/api/folders', {
            headers: {
              'Authorization': `Bearer ${token}`,
            },
          });
          
          // Flatten nested folders for dropdown
          const flattenFolders = (folders, prefix = '') => {
            let result = [];
            folders.forEach(folder => {
              const displayName = prefix ? `${prefix} > ${folder.folder_name}` : folder.folder_name;
              result.push({ id: folder.id, displayName, level: prefix.split(' > ').length - 1 || 0 });
              if (folder.subfolders && folder.subfolders.length > 0) {
                result = result.concat(flattenFolders(folder.subfolders, displayName));
              }
            });
            return result;
          };
          
          const flatFolders = flattenFolders(response.data.folders);
          setAllFolders(flatFolders);
        } catch (error) {
          console.error('Error fetching all folders:', error);
        }
      }
    };

    // Fetch available folders for parent selection
    const fetchAvailableFolders = async () => {
      if (isAuthenticated) {
        try {
          const token = await getAccessTokenSilently({
            authorizationParams: {
              audience: process.env.REACT_APP_AUTH0_AUDIENCE,
              scope: 'read:folders',
            },
          });
          const response = await api.get('/api/folders', {
            headers: {
              'Authorization': `Bearer ${token}`,
            },
          });
          // Flatten nested folders for dropdown
          const flattenFolders = (folders, prefix = '') => {
            let result = [];
            folders.forEach(folder => {
              const displayName = prefix ? `${prefix} > ${folder.folder_name}` : folder.folder_name;
              result.push({ id: folder.id, displayName, level: prefix.split(' > ').length - 1 || 0 });
              if (folder.subfolders && folder.subfolders.length > 0) {
                result = result.concat(flattenFolders(folder.subfolders, displayName));
              }
            });
            return result;
          };
          
          const flatFolders = flattenFolders(response.data.folders);
          setAvailableFolders(flatFolders);
        } catch (error) {
          console.error('Error fetching available folders:', error);
        }
      }
    };
    
    fetchAllFolders();
    fetchAvailableFolders();
  }, [isAuthenticated, user, getAccessTokenSilently, api]);

  const handleUpload = async (event) => {
    event.preventDefault();
    if (files.length === 0 || !author || !category) {
      alert("Please submit all fields");
      return;
    }

    try {
      if (!isAuthenticatedState) {
        console.error('User is not authenticated');
        alert('You need to be logged in to upload files.');
        await loginWithRedirect();
        return;
      }

      let token;
      try {
        token = await getAccessTokenSilently({
          authorizationParams: {
            audience: process.env.REACT_APP_AUTH0_AUDIENCE,
            scope: "create:files create:folders"
          }
        });
      } catch (tokenError) {
        console.error('Failed to get access token:', tokenError);
        alert('Your session has expired. Please log in again.');
        await loginWithRedirect();
        return;
      }

      if (!token) {
        console.error('Failed to retrieve access token.');
        return;
      }
        const formData = new FormData();
        files.forEach(file => {
          formData.append('files', file); // Ensure the field name is 'files'
        });
        formData.append('author', author);
        formData.append('uploadDate', uploadDate);
        formData.append('country', country);
        formData.append('category', category);
        formData.append('isPublic', isPublic);
        formData.append('workpackage', workpackage || null); // Handle empty workpackage
        formData.append('isMeeting', isMeeting);
        formData.append('isDeliverable', isDeliverable);
        formData.append('isContactList', isContactList);
        formData.append('isPromotion', isPromotion);
        formData.append('isReport', isReport);
        formData.append('isPublication', isPublication);
        formData.append('isTemplate', isTemplate);
        if (folderName.trim()) {
          formData.append('folderName', folderName.trim()); // Add folder name to form data if provided
          formData.append('allowSharedAccess', allowSharedAccess); // Add shared access setting for folders
          if (selectedParentFolder) {
            formData.append('parentFolderId', selectedParentFolder); // Add parent folder ID if selected
          }
        }
  
      const response = await api.post('/api/uploads', formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.status === 200) {
        alert('Files uploaded successfully');
        navigate('/search');
      }
    } catch (error) {
      console.error('Error uploading files:', error);
      alert('An error occurred while uploading files. Please try again.');
    }
  };

  const handleUploadToExistingFolder = async (event) => {
    event.preventDefault();
    if (files.length === 0 || !selectedUploadFolder) {
      alert("Please select files and a folder");
      return;
    }

    try {
      let token;
      try {
        token = await getAccessTokenSilently({
          authorizationParams: {
            audience: process.env.REACT_APP_AUTH0_AUDIENCE,
            scope: "create:files"
          }
        });
      } catch (tokenError) {
        console.error('Failed to get access token:', tokenError);
        alert('Your session has expired. Please log in again.');
        await loginWithRedirect();
        return;
      }

      const formData = new FormData();
      files.forEach(file => {
        formData.append('files', file);
      });
      formData.append('author', author);
      formData.append('uploadDate', uploadDate);
      formData.append('country', country);
      formData.append('category', category);
      formData.append('isPublic', isPublic);
      formData.append('workpackage', workpackage || null);
      formData.append('isMeeting', isMeeting);
      formData.append('isDeliverable', isDeliverable);
      formData.append('isContactList', isContactList);
      formData.append('isPromotion', isPromotion);
      formData.append('isReport', isReport);
      formData.append('isPublication', isPublication);
      formData.append('isTemplate', isTemplate);

      const response = await api.post(`/api/upload-to-folder/${selectedUploadFolder}`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.status === 200) {
        alert('Files uploaded successfully to folder');
        navigate('/search');
      }
    } catch (error) {
      console.error('Error uploading files to folder:', error);
      if (error.response?.status === 403) {
        alert('Access denied. You do not have permission to upload to this folder.');
      } else {
        alert('An error occurred while uploading files. Please try again.');
      }
    }
  };

  const currentDate = new Date().toISOString().split('T')[0];

  const europeanCountries = [
    'Albania', 'Andorra', 'Austria', 'Belarus', 'Belgium', 'Bosnia and Herzegovina', 'Bulgaria', 'Croatia', 'Cyprus', 'Czech Republic', 'Denmark', 'Estonia', 'Finland', 'France', 'Germany', 'Greece', 'Hungary', 'Iceland', 'Ireland', 'Italy', 'Kosovo', 'Latvia', 'Liechtenstein', 'Lithuania', 'Luxembourg', 'Malta', 'Moldova', 'Monaco', 'Montenegro', 'Netherlands', 'North Macedonia (formerly Macedonia)', 'Norway', 'Poland', 'Portugal', 'Romania', 'Russia', 'San Marino', 'Serbia', 'Slovakia', 'Slovenia', 'Spain', 'Sweden', 'Switzerland', 'Ukraine', 'United Kingdom (UK)', 'Vatican City (Holy See)',
  ];

  return (
    <div className="Center">
      <div className="upload-container">
        <form encType='multipart/form-data' className="clr-form main-upload-form" clrLayout="horizontal" onSubmit={handleUpload}>
        <label htmlFor="files">Upload Files</label>
        <div className="file-input-container">
          <input
            type="file"
            id="files"
            name='files'
            accept=".pdf, .ppt, .pptx, .doc, .docx, .xls, .xlsx, .csv, .txt, .jpg, .jpeg, .png"
            multiple
            onChange={e => setFiles(Array.from(e.target.files))}
            className="file-input"
          />
          <label htmlFor="files" className="file-input-label">
            {files.length > 0 ? `${files.length} file(s) selected` : 'Click to choose files'}
          </label>
        </div>
        <label htmlFor="folderName">Folder Name (optional)</label>
        <input
          type="text"
          id="folderName"
          placeholder="Leave empty for individual files"
          value={folderName}
          onChange={e => setFolderName(e.target.value)}
        />
        {folderName.trim() && (
          <div style={{ marginTop: '10px', marginBottom: '20px' }}>
            <label htmlFor="parentFolder" style={{ color: 'white', display: 'block', marginBottom: '5px' }}>
              Parent Folder (optional)
            </label>
            <select
              id="parentFolder"
              value={selectedParentFolder}
              onChange={e => setSelectedParentFolder(e.target.value)}
              style={{
                width: '100%',
                padding: '8px',
                marginBottom: '10px',
                borderRadius: '4px',
                border: '1px solid #ccc'
              }}
            >
              <option value="">-- Create as top-level folder --</option>
              {availableFolders.map(folder => (
                <option key={folder.id} value={folder.id}>
                  {'  '.repeat(folder.level)}📁 {folder.displayName}
                </option>
              ))}
            </select>
            
            <div className="clr-checkbox">
              <input
                type="checkbox"
                id="allowSharedAccess"
                checked={allowSharedAccess}
                onChange={e => setAllowSharedAccess(e.target.checked)}
              />
              <label style={{ color: 'white' }} htmlFor="allowSharedAccess">
                - Allow editors and admins to upload files to this folder
              </label>
            </div>
          </div>
        )}
        <div className="horizontal-container">
          <select
            id="workpackage"
            value={workpackage}
            onChange={e => setWorkpackage(e.target.value)}
          >
            <option value="">None</option>
            <option value="WP1">WP1</option>
            <option value="WP2">WP2</option>
            <option value="WP3">WP3</option>
            <option value="WP4">WP4</option>
            <option value="WP5">WP5</option>
            <option value="WP6">WP6</option>
          </select>
          <label style={{ color: 'white' }} htmlFor="workpackage">-- Select Workpackage for this upload</label>
        </div>
        <hr className="divider" />
        <div style={{ color: 'white', marginBottom: '5px', textDecorationStyle: 'solid' }}>
          Check any selection relevant for this file
        </div>
        <div className="checkbox-container">
          <input
            type="checkbox"
            id="isPublic"
            checked={isPublic}
            name="isPublic"
            onChange={e => setIsPublic(e.target.checked)}
          />
          <label style={{ color: 'white' }} htmlFor="isPublic">- Public Document</label>
        </div>
        <div className="clr-checkbox">
          <input
            type="checkbox"
            checked={isMeeting}
            id="isMeeting"
            name="isMeeting"
            onChange={e => setIsMeeting(e.target.checked)}
          />
          <label style={{ color: 'white' }} htmlFor="isMeeting">- Meeting</label>
        </div>
        <div className="clr-checkbox">
          <input
            type="checkbox"
            checked={isDeliverable}
            id="isDeliverable"
            name="isDeliverable"
            onChange={e => setIsDeliverable(e.target.checked)}
          />
          <label style={{ color: 'white' }} htmlFor="isDeliverable">- Deliverable</label>
        </div>
        <div className="clr-checkbox">
          <input
            type="checkbox"
            checked={isContactList}
            id="contactList"
            name="contactList"
            onChange={e => setIsContactList(e.target.checked)}
          />
          <label style={{ color: 'white' }} htmlFor="contactList">- Contact List</label>
        </div>
        <div className="clr-checkbox">
          <input
            type="checkbox"
            checked={isPromotion}
            id="isPromotion"
            name="isPromotion"
            onChange={e => setIsPromotion(e.target.checked)}
          />
          <label style={{ color: 'white' }} htmlFor="isPromotion">- Promotion Material</label>
        </div>
        <div className="clr-checkbox">
          <input
            type="checkbox"
            checked={isReport}
            id="isReport"
            name="isReport"
            onChange={e => setIsReport(e.target.checked)}
          />
          <label style={{ color: 'white' }} htmlFor="isReport">- Report</label>
        </div>
        <div className="clr-checkbox">
          <input
            type="checkbox"
            checked={isPublication}
            id="isPublication"
            name="isPublication"
            onChange={e => setIsPublication(e.target.checked)}
          />
          <label style={{ color: 'white' }} htmlFor="isPublication">- Publication</label>
        </div>
        <div className="clr-checkbox">
          <input
            type="checkbox"
            checked={isTemplate}
            id="isTemplate"
            name="isTemplate"
            onChange={e => setIsTemplate(e.target.checked)}
          />
          <label style={{ color: 'white' }} htmlFor="isTemplate">- Template</label>
        </div>

        <label htmlFor="category">Title</label>
        <input
          type="text"
          id="category"
          placeholder="Title"
          value={category}
          onChange={e => setCategory(e.target.value)}
          autoFocus
        />
        <label htmlFor="author">Author</label>
        <input
          type="text"
          id="author"
          placeholder="Author"
          value={author}
          readOnly // Make the author field read-only
        />

        <label htmlFor="uploadDate">Upload Date</label>
        <input
          type="text"
          id="uploadDate"
          placeholder="Upload Date"
          value={currentDate}
          onChange={e => setUploadDate(e.target.value)}
          autoFocus
        />

        <label htmlFor="country">Country</label>
        <select
          id="country"
          value={country}
          onChange={e => setCountry(e.target.value)}
        >
          <option value="">Select a country</option>
          {europeanCountries.map(country => (
            <option key={country} value={country}>
              {country}
            </option>
          ))}
        </select>

          <CdsButton className="clr-button" type='submit' onClick={(e) => {
              if (isPublic && !window.confirm('You have chosen to make this file public, are you sure?')) {
              e.preventDefault();
              setIsPublic(false);
              }
          }}>
            Upload
          </CdsButton>
        </form>

        <div className="upload-mode-section">
          <div className="vertical-divider">
            <span className="divider-text">OR</span>
          </div>
          <h3 className="upload-mode-title" style={{ color: 'white', textAlign: 'center', marginBottom: '20px' }}>
            Upload to Existing Folder
          </h3>
          
          <form encType='multipart/form-data' className="clr-form existing-folder-form" clrLayout="horizontal" onSubmit={handleUploadToExistingFolder}>
            <label htmlFor="existingFiles" style={{ color: 'white' }}>Upload Files to Existing Folder</label>
            <div className="file-input-container">
              <input
                type="file"
                id="existingFiles"
                name='files'
                accept=".pdf, .ppt, .pptx, .doc, .docx, .xls, .xlsx, .csv, .txt, .jpg, .jpeg, .png"
                multiple
                onChange={e => setFiles(Array.from(e.target.files))}
                className="file-input"
              />
              <label htmlFor="existingFiles" className="file-input-label">
                {files.length > 0 ? `${files.length} file(s) selected` : 'Click to choose files'}
              </label>
            </div>
            
            <label htmlFor="uploadFolder" style={{ color: 'white' }}>Select Folder</label>
            <select
              id="uploadFolder"
              className="upload-folder-select"
              value={selectedUploadFolder}
              onChange={e => setSelectedUploadFolder(e.target.value)}
              required
              style={{
                width: '100%',
                padding: '8px',
                marginBottom: '20px',
                borderRadius: '4px',
                border: '1px solid #ccc'
              }}
            >
              <option value="">Select a folder</option>
              {allFolders.map(folder => (
                <option key={folder.id} value={folder.id}>
                  {'  '.repeat(folder.level)}📁 {folder.displayName}
                </option>
              ))}
            </select>
            
            <CdsButton className="clr-button upload-folder-button" type='submit'>
              Upload to Folder
            </CdsButton>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Upload;